import { ReactElement } from 'react';
import { Alert, Box, Chip, Divider, Paper, Typography } from '@mui/material';
import { IOrganisationTeamsResponse } from '../../types/@organisation';

interface IProps {
  teams: IOrganisationTeamsResponse;
}

const OrganisationTeamsPanel = ({ teams }: IProps): ReactElement => {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Typography sx={{ fontSize: '24px', fontWeight: 700, color: '#17324d' }}>
          Teams
        </Typography>
        <Chip
          label={teams.source === 'qdos' ? 'Source: QDOS' : 'Source unavailable'}
          color={teams.source === 'qdos' ? 'primary' : 'default'}
        />
      </Box>

      {teams.warnings?.length > 0 && (
        <Alert severity="warning">
          {teams.warnings.map((warning, index) => (
            <Box key={index}>{warning}</Box>
          ))}
        </Alert>
      )}

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'repeat(4, 1fr)' },
          gap: 2,
        }}
      >
        <Paper sx={{ p: 2, borderRadius: '14px' }} elevation={0} variant="outlined">
          <Typography sx={{ fontSize: '13px', color: '#6b7c8f', mb: 1 }}>Teams</Typography>
          <Typography sx={{ fontSize: '24px', fontWeight: 700, color: '#17324d' }}>
            {teams.summary.teamCount}
          </Typography>
        </Paper>

        <Paper sx={{ p: 2, borderRadius: '14px' }} elevation={0} variant="outlined">
          <Typography sx={{ fontSize: '13px', color: '#6b7c8f', mb: 1 }}>Player shirts</Typography>
          <Typography sx={{ fontSize: '24px', fontWeight: 700, color: '#17324d' }}>
            {teams.summary.totalPlayerShirts}
          </Typography>
        </Paper>

        <Paper sx={{ p: 2, borderRadius: '14px' }} elevation={0} variant="outlined">
          <Typography sx={{ fontSize: '13px', color: '#6b7c8f', mb: 1 }}>Fan shirts</Typography>
          <Typography sx={{ fontSize: '24px', fontWeight: 700, color: '#17324d' }}>
            {teams.summary.totalFanShirts}
          </Typography>
        </Paper>

        <Paper sx={{ p: 2, borderRadius: '14px' }} elevation={0} variant="outlined">
          <Typography sx={{ fontSize: '13px', color: '#6b7c8f', mb: 1 }}>Mismatches</Typography>
          <Typography sx={{ fontSize: '24px', fontWeight: 700, color: '#17324d' }}>
            {teams.summary.mismatchCount}
          </Typography>
        </Paper>
      </Box>

      {teams.teams.length ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {teams.teams.map((team) => {
            const healthy = team.playersConsistent && team.fansConsistent;

            return (
              <Paper
                key={team.teamId}
                sx={{
                  p: 3,
                  borderRadius: '16px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2,
                }}
                elevation={0}
                variant="outlined"
              >
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: { xs: 'flex-start', md: 'center' },
                    flexDirection: { xs: 'column', md: 'row' },
                    gap: 1,
                  }}
                >
                  <Box>
                    <Typography sx={{ fontSize: '20px', fontWeight: 700, color: '#17324d' }}>
                      {team.name}
                    </Typography>
                    <Typography sx={{ fontSize: '13px', color: '#6b7c8f' }}>
                      Team ID: {team.teamId}
                    </Typography>
                  </Box>

                  <Chip
                    label={healthy ? 'Healthy' : 'Needs attention'}
                    color={healthy ? 'success' : 'warning'}
                  />
                </Box>

                <Divider />

                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                    gap: 2,
                  }}
                >
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: '12px',
                      border: '1px solid #e3e8ef',
                      background: '#fafbfd',
                    }}
                  >
                    <Typography sx={{ fontSize: '14px', fontWeight: 700, color: '#17324d', mb: 1 }}>
                      Players
                    </Typography>
                    <Typography sx={{ fontSize: '14px', color: '#5f7285' }}>
                      QDOS items: {team.players.qCount}
                    </Typography>
                    <Typography sx={{ fontSize: '14px', color: '#5f7285' }}>
                      XDOS things: {team.players.xCount}
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      p: 2,
                      borderRadius: '12px',
                      border: '1px solid #e3e8ef',
                      background: '#fafbfd',
                    }}
                  >
                    <Typography sx={{ fontSize: '14px', fontWeight: 700, color: '#17324d', mb: 1 }}>
                      Fans
                    </Typography>
                    <Typography sx={{ fontSize: '14px', color: '#5f7285' }}>
                      QDOS items: {team.fans.qCount}
                    </Typography>
                    <Typography sx={{ fontSize: '14px', color: '#5f7285' }}>
                      XDOS things: {team.fans.xCount}
                    </Typography>
                  </Box>
                </Box>

                {team.warnings.length > 0 && (
                  <Alert severity="warning">
                    {team.warnings.map((warning, index) => (
                      <Box key={index}>{warning}</Box>
                    ))}
                  </Alert>
                )}
              </Paper>
            );
          })}
        </Box>
      ) : (
        <Alert severity="info">No team data is currently available for this organisation.</Alert>
      )}
    </Box>
  );
};

export default OrganisationTeamsPanel;